import { CheckCircle2, Globe, Plus, ShieldCheck, Smartphone } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createTenantDomain,
  getReleaseAppNativeIdentity,
  listCustomEnvironments,
  listTenantDomains,
  upsertReleaseAppNativeIdentity,
  verifyTenantDomain,
  type CustomEnvironmentRow,
  type ReleaseAppNativeIdentityRow,
  type TenantDomainRow,
} from '../api';
import { StatusChip } from '../components/Avatar';
import { CopyButton } from '../components/CopyButton';
import { EmptyState } from '../components/EmptyState';
import { useFeedback } from '../contexts/FeedbackContext';
import { useSession } from '../contexts/SessionContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatRelativeDate } from '../utils/formatters';

const DOMAIN_CHALLENGE_PATH = '/.well-known/cindro-domain-verification';

type RequestError = {
  response?: { data?: { message?: string } };
  message?: string;
};

function messageFrom(error: unknown, fallback: string) {
  const failure = error as RequestError;
  return failure?.response?.data?.message || failure?.message || fallback;
}

function statusTone(status: TenantDomainRow['verificationStatus']) {
  if (status === 'verified') return 'success' as const;
  if (status === 'failed') return 'warn' as const;
  return 'neutral' as const;
}

function DomainsPage() {
  const { session } = useSession();
  const { toast } = useFeedback();
  const { activeOrganization, activeProject, workspace } = useWorkspace();
  const { releaseApps, environments } = workspace;

  const [domains, setDomains] = useState<TenantDomainRow[]>([]);
  const [customEnvironments, setCustomEnvironments] = useState<CustomEnvironmentRow[]>([]);
  const [identities, setIdentities] = useState<Record<string, ReleaseAppNativeIdentityRow | null>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [verifyingId, setVerifyingId] = useState('');
  const [showCreateDomain, setShowCreateDomain] = useState(false);
  const [newHostname, setNewHostname] = useState('');
  const [newEnvironment, setNewEnvironment] = useState('production');
  const [creatingDomain, setCreatingDomain] = useState(false);
  const [editingAppId, setEditingAppId] = useState('');
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [identityForm, setIdentityForm] = useState({
    bundleId: '',
    appleTeamId: '',
    appleAppStoreId: '',
    androidCertSha256Fingerprints: '',
    customUrlScheme: '',
    deepLinkPaths: '*',
  });

  const environmentOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...environments
            .map((environment) => environment.slug)
            .filter((environment) => environment !== 'internal'),
          ...customEnvironments.map((environment) => environment.slug),
          'dev',
          'staging',
          'production',
        ])
      ),
    [customEnvironments, environments]
  );

  const loadConfiguration = useCallback(async () => {
    if (!session || !activeOrganization) return;
    setLoading(true);
    setLoadError('');
    try {
      const [domainRows, customRows, identityEntries] = await Promise.all([
        listTenantDomains(session.accessToken, {
          organizationId: activeOrganization.id,
          projectId: activeProject?.id,
        }),
        listCustomEnvironments(session.accessToken, {
          organizationId: activeOrganization.id,
          projectId: activeProject?.id,
        }).catch(() => []),
        Promise.all(
          releaseApps.map(async (app) => [
            app.releaseAppId,
            await getReleaseAppNativeIdentity(session.accessToken, app.releaseAppId),
          ] as const)
        ),
      ]);
      setDomains(domainRows);
      setCustomEnvironments(customRows);
      setIdentities(Object.fromEntries(identityEntries));
    } catch (error: unknown) {
      setLoadError(messageFrom(error, 'Domain configuration could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [activeOrganization, activeProject?.id, releaseApps, session]);

  useEffect(() => {
    void loadConfiguration();
  }, [loadConfiguration]);

  const verifiedCount = domains.filter((domain) => domain.verificationStatus === 'verified').length;
  const identityCount = releaseApps.filter((app) => Boolean(identities[app.releaseAppId])).length;

  async function handleCreateDomain() {
    if (!session || !activeOrganization || creatingDomain) return;
    const hostname = newHostname.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
    if (!hostname) return;
    setCreatingDomain(true);
    try {
      const created = await createTenantDomain(session.accessToken, {
        organizationId: activeOrganization.id,
        projectId: activeProject?.id,
        environment: newEnvironment,
        hostname,
      });
      setDomains((rows) => [created, ...rows]);
      setNewHostname('');
      setShowCreateDomain(false);
      toast('success', `${hostname} claimed. Publish the challenge token to verify it.`);
    } catch (error: unknown) {
      toast('error', messageFrom(error, 'Unable to claim the domain.'));
    } finally {
      setCreatingDomain(false);
    }
  }

  async function handleVerifyDomain(domain: TenantDomainRow) {
    if (!session || verifyingId) return;
    setVerifyingId(domain.tenantDomainId);
    try {
      const updated = await verifyTenantDomain(session.accessToken, domain.tenantDomainId);
      setDomains((rows) => rows.map((row) => row.tenantDomainId === updated.tenantDomainId ? updated : row));
      toast(
        updated.verificationStatus === 'verified' ? 'success' : 'error',
        updated.verificationStatus === 'verified'
          ? `${updated.hostname} is verified`
          : `Cindro could not read the challenge token from ${updated.hostname}`
      );
    } catch (error: unknown) {
      toast('error', messageFrom(error, 'Unable to verify the domain.'));
    } finally {
      setVerifyingId('');
    }
  }

  function openIdentityEditor(releaseAppId: string) {
    const existing = identities[releaseAppId];
    setIdentityForm({
      bundleId: existing?.bundleId || '',
      appleTeamId: existing?.appleTeamId || '',
      appleAppStoreId: existing?.appleAppStoreId || '',
      androidCertSha256Fingerprints: (existing?.androidCertSha256Fingerprints || []).join(', '),
      customUrlScheme: existing?.customUrlScheme || '',
      deepLinkPaths: (existing?.deepLinkPaths || ['*']).join(', '),
    });
    setEditingAppId(releaseAppId);
  }

  async function saveIdentity(releaseAppId: string) {
    if (!session || !activeOrganization || savingIdentity) return;
    const bundleId = identityForm.bundleId.trim();
    const customUrlScheme = identityForm.customUrlScheme.trim();
    if (!bundleId || !customUrlScheme) return;
    setSavingIdentity(true);
    try {
      const saved = await upsertReleaseAppNativeIdentity(session.accessToken, releaseAppId, {
        releaseAppId,
        organizationId: activeOrganization.id,
        bundleId,
        appleTeamId: identityForm.appleTeamId.trim() || undefined,
        appleAppStoreId: identityForm.appleAppStoreId.trim() || undefined,
        androidCertSha256Fingerprints: identityForm.androidCertSha256Fingerprints
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean),
        customUrlScheme,
        deepLinkPaths: identityForm.deepLinkPaths
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean),
      });
      setIdentities((current) => ({ ...current, [releaseAppId]: saved }));
      setEditingAppId('');
      toast('success', 'Native app identity saved');
    } catch (error: unknown) {
      toast('error', messageFrom(error, 'Unable to save the native app identity.'));
    } finally {
      setSavingIdentity(false);
    }
  }

  return (
    <div className="configurationPage">
      <div className="configurationSummary" aria-label="Domain readiness summary">
        <div>
          <span>Verified domains</span>
          <strong>{verifiedCount} / {domains.length}</strong>
          <small>Trusted for deep links and CORS</small>
        </div>
        <div>
          <span>Native identities</span>
          <strong>{identityCount} / {releaseApps.length}</strong>
          <small>Platform tracks configured</small>
        </div>
        <div>
          <span>Attention needed</span>
          <strong>{domains.length - verifiedCount + releaseApps.length - identityCount}</strong>
          <small>Incomplete configuration items</small>
        </div>
      </div>

      {loadError ? (
        <div className="configurationError" role="alert">
          <span>{loadError}</span>
          <button type="button" className="secondaryButton" onClick={() => void loadConfiguration()}>Retry</button>
        </div>
      ) : null}

      <section className="configurationSection" aria-labelledby="tenant-domains-title">
        <header className="configurationSectionHeader">
          <div>
            <div className="panelKicker">Web entry points</div>
            <h2 id="tenant-domains-title">Domains</h2>
            <p>Verify ownership before Cindro publishes deep-link metadata or trusts the host for CORS.</p>
          </div>
          {domains.length > 0 || showCreateDomain ? (
            <button type="button" className="panelControl" onClick={() => setShowCreateDomain((open) => !open)}>
              <Plus size={14} />
              {showCreateDomain ? 'Cancel' : 'Claim domain'}
            </button>
          ) : null}
        </header>

        {showCreateDomain ? (
          <div className="configurationInlineForm domainClaimForm" aria-label="Claim a domain">
            <label>
              <span>Hostname</span>
              <input
                value={newHostname}
                onChange={(event) => setNewHostname(event.target.value)}
                placeholder="app.example.com"
                aria-label="New domain hostname"
              />
            </label>
            <label>
              <span>Environment</span>
              <select value={newEnvironment} onChange={(event) => setNewEnvironment(event.target.value)}>
                {environmentOptions.map((environment) => <option key={environment} value={environment}>{environment}</option>)}
              </select>
            </label>
            <button
              type="button"
              className="primaryButton"
              disabled={creatingDomain || newHostname.trim().length < 3}
              onClick={handleCreateDomain}
            >
              <Globe size={14} />
              {creatingDomain ? 'Claiming…' : 'Claim'}
            </button>
          </div>
        ) : null}

        {domains.length > 0 ? (
          <div className="configurationList domainConfigurationList">
            {domains.map((domain) => (
              <div className="domainConfigurationRow" key={domain.tenantDomainId}>
                <div className="domainConfigurationMain">
                  <span className="serviceMark"><Globe size={14} /></span>
                  <div className="configurationListPrimary">
                    <strong>{domain.hostname}</strong>
                    <span>{domain.environment || 'All environments'}</span>
                  </div>
                  <StatusChip tone={statusTone(domain.verificationStatus)}>{domain.verificationStatus}</StatusChip>
                  <span className="domainVerifiedAt">
                    {domain.verifiedAt ? `Verified ${formatRelativeDate(domain.verifiedAt)}` : 'Not verified'}
                  </span>
                  {domain.verificationStatus !== 'verified' ? (
                    <button
                      type="button"
                      className="secondaryButton"
                      disabled={verifyingId === domain.tenantDomainId}
                      onClick={() => void handleVerifyDomain(domain)}
                    >
                      <ShieldCheck size={13} />
                      {verifyingId === domain.tenantDomainId ? 'Checking…' : 'Check now'}
                    </button>
                  ) : (
                    <span className="configurationReady"><CheckCircle2 size={14} /> Ready</span>
                  )}
                </div>
                {domain.verificationStatus !== 'verified' ? (
                  <div className="domainChallenge">
                    <div>
                      <span>Publish this token as plain text at</span>
                      <code>https://{domain.hostname}{DOMAIN_CHALLENGE_PATH}</code>
                    </div>
                    <div className="domainChallengeToken">
                      <code>{domain.verificationToken}</code>
                      <CopyButton value={domain.verificationToken} label="Copy token" />
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : !loading && !loadError ? (
          <EmptyState
            icon={<Globe size={20} />}
            title="No domains claimed"
            description="Claim the hostname your users will open for web-to-app links."
            action={<button type="button" className="secondaryButton" onClick={() => setShowCreateDomain(true)}>Claim domain</button>}
          />
        ) : loading ? <div className="configurationLoading">Loading domains…</div> : null}
      </section>

      <section className="configurationSection" aria-labelledby="native-identities-title">
        <header className="configurationSectionHeader">
          <div>
            <div className="panelKicker">App destinations</div>
            <h2 id="native-identities-title">Native app identities</h2>
            <p>Connect each iOS or Android track to the identifiers used by Apple and Google deep-link verification.</p>
          </div>
        </header>

        {releaseApps.length > 0 ? (
          <div className="configurationList">
            {releaseApps.map((app) => {
              const identity = identities[app.releaseAppId];
              const isEditing = editingAppId === app.releaseAppId;
              return (
                <div className="nativeIdentityRow" key={app.releaseAppId}>
                  <div className="nativeIdentitySummary">
                    <span className="serviceMark"><Smartphone size={14} /></span>
                    <div className="configurationListPrimary">
                      <strong>{app.name}</strong>
                      <span>{app.platform.toUpperCase()} · {app.environment || 'Environment not assigned'}</span>
                    </div>
                    <div className="configurationListMeta">
                      <span>App identifier</span>
                      <strong>{identity?.bundleId || 'Not configured'}</strong>
                    </div>
                    <div className="configurationListMeta">
                      <span>URL scheme</span>
                      <strong>{identity?.customUrlScheme || '—'}</strong>
                    </div>
                    <button type="button" className="secondaryButton" onClick={() => isEditing ? setEditingAppId('') : openIdentityEditor(app.releaseAppId)}>
                      {isEditing ? 'Cancel' : identity ? 'Edit' : 'Configure'}
                    </button>
                  </div>

                  {isEditing ? (
                    <div className="configurationEditor">
                      <label>
                        <span>Bundle ID / application ID</span>
                        <input value={identityForm.bundleId} onChange={(event) => setIdentityForm((form) => ({ ...form, bundleId: event.target.value }))} placeholder="com.example.app" />
                      </label>
                      {app.platform === 'ios' ? (
                        <>
                          <label>
                            <span>Apple Team ID</span>
                            <input value={identityForm.appleTeamId} onChange={(event) => setIdentityForm((form) => ({ ...form, appleTeamId: event.target.value }))} placeholder="55Q53B4W2H" />
                          </label>
                          <label>
                            <span>App Store ID</span>
                            <input value={identityForm.appleAppStoreId} onChange={(event) => setIdentityForm((form) => ({ ...form, appleAppStoreId: event.target.value }))} placeholder="6793042413" />
                          </label>
                        </>
                      ) : (
                        <label className="configurationEditorWide">
                          <span>SHA-256 certificate fingerprints</span>
                          <input value={identityForm.androidCertSha256Fingerprints} onChange={(event) => setIdentityForm((form) => ({ ...form, androidCertSha256Fingerprints: event.target.value }))} placeholder="AA:BB:CC:… (comma separated)" />
                        </label>
                      )}
                      <label>
                        <span>Custom URL scheme</span>
                        <input value={identityForm.customUrlScheme} onChange={(event) => setIdentityForm((form) => ({ ...form, customUrlScheme: event.target.value }))} placeholder="skillora" />
                      </label>
                      <label className="configurationEditorWide">
                        <span>Allowed deep-link paths</span>
                        <input value={identityForm.deepLinkPaths} onChange={(event) => setIdentityForm((form) => ({ ...form, deepLinkPaths: event.target.value }))} placeholder="*" />
                        <small>Use * for every path, or comma-separated patterns such as /accept-invite*.</small>
                      </label>
                      <div className="configurationEditorActions">
                        <button
                          type="button"
                          className="primaryButton"
                          disabled={savingIdentity || identityForm.bundleId.trim().length < 2 || identityForm.customUrlScheme.trim().length < 2}
                          onClick={() => void saveIdentity(app.releaseAppId)}
                        >
                          {savingIdentity ? 'Saving…' : 'Save identity'}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : !loading ? (
          <EmptyState
            icon={<Smartphone size={20} />}
            title="No native app tracks"
            description="Add an iOS or Android track under App versions before configuring deep links."
          />
        ) : <div className="configurationLoading">Loading app identities…</div>}
      </section>
    </div>
  );
}

export { DomainsPage };
